import { Close, ExpandMore, InfoOutlined } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Drawer,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import axios from "axios";
import {
  formatPermissionLabel,
  getPermissionDescription,
  groupPermissionsByModule,
  isDefaultMemberPermissionKey,
  isV2PermissionKey,
  filterMembershipPermissionKeys,
  isPlatformScopedPermissionKey,
} from "const/permissions";
import { ROLE_PRESETS, findRolePreset, resolveRolePresetKeys } from "const/rolePermissionPresets";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

const permissionNamesFromUser = (user) => {
  if (user?.permissionKeys?.length) {
    return user.permissionKeys.filter(isV2PermissionKey);
  }
  const source = user?.permittedModules ?? user?.permissions ?? [];
  return source
    .map((item) => (typeof item === "string" ? item : item?.name))
    .filter(isV2PermissionKey);
};

export default function PermissionDrawer({
  open,
  setOpen,
  data,
  setData,
  permissionList,
  refetchUsers,
  membershipContext,
  onPermissionsSaved,
}) {
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [expandedModules, setExpandedModules] = useState([]);

  const activeContext = membershipContext || data;

  const membershipPermissionList = useMemo(
    () => (permissionList || []).filter((p) => !isPlatformScopedPermissionKey(p?.name)),
    [permissionList]
  );

  const moduleGroups = useMemo(
    () => groupPermissionsByModule(membershipPermissionList),
    [membershipPermissionList]
  );

  const [isUsingDefaults, setIsUsingDefaults] = useState(false);
  const [presetRole, setPresetRole] = useState("");
  const [pendingPresetRole, setPendingPresetRole] = useState(null);
  const [appliedPresetLabel, setAppliedPresetLabel] = useState(null);

  useEffect(() => {
    const existing = filterMembershipPermissionKeys(permissionNamesFromUser(activeContext));
    if (existing.length) {
      setSelectedKeys(existing);
      setIsUsingDefaults(false);
      return;
    }
    // Nobody has set anything yet — start from the standard day-to-day modules.
    const defaults = moduleGroups
      .flatMap((group) => group.permissions.map((p) => p.name))
      .filter(isDefaultMemberPermissionKey);
    setSelectedKeys(defaults);
    setIsUsingDefaults(defaults.length > 0);
  }, [
    activeContext?.permittedModules,
    activeContext?.permissions,
    activeContext?.permissionKeys,
    membershipContext,
    moduleGroups,
  ]);

  // Every module starts collapsed each time the drawer is opened.
  useEffect(() => {
    if (!open) setExpandedModules([]);
  }, [open]);

  // The picker starts on the role this membership already has, so the common case is one click.
  useEffect(() => {
    if (!open) {
      return;
    }
    // Membership rows carry `role`; the single-org form carries the same value as `type`.
    const role = String(activeContext?.role || activeContext?.type || "").toUpperCase();
    setPresetRole(findRolePreset(role) ? role : "");
    setAppliedPresetLabel(null);
    setPendingPresetRole(null);
  }, [open, activeContext?.role, activeContext?.type]);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const toggleModuleExpanded = (moduleKey) =>
    setExpandedModules((prev) =>
      prev.includes(moduleKey) ? prev.filter((k) => k !== moduleKey) : [...prev, moduleKey]
    );

  const allCatalogKeys = useMemo(
    () => moduleGroups.flatMap((group) => group.permissions.map((p) => p.name)),
    [moduleGroups]
  );

  const pendingPreset = pendingPresetRole ? findRolePreset(pendingPresetRole) : null;

  const applyPreset = (role) => {
    const preset = findRolePreset(role);
    if (!preset) {
      return;
    }
    setSelectedKeys(resolveRolePresetKeys(preset, allCatalogKeys));
    setAppliedPresetLabel(preset.label);
    setIsUsingDefaults(false);
    setPendingPresetRole(null);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedKeys([...allCatalogKeys]);
    } else {
      setSelectedKeys([]);
    }
  };

  const toggleKey = (key) => {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleModule = (group, checked) => {
    const keys = group.permissions.map((p) => p.name);
    setSelectedKeys((prev) => {
      if (checked) {
        return [...new Set([...prev, ...keys])];
      }
      return prev.filter((k) => !keys.includes(k));
    });
  };

  const handleSave = () => {
    const payload = filterMembershipPermissionKeys(selectedKeys);
    const membershipId = activeContext?.membershipId;
    const shouldSaveLocally = onPermissionsSaved && (!data?.id || !membershipId);

    if (shouldSaveLocally) {
      onPermissionsSaved(payload);
      setOpen(false);
      return;
    }

    if (data?.id) {
      const orgId = activeContext?.orgId ?? data?.orgId;
      const orgQuery = orgId ? `?orgId=${orgId}` : "";
      const saveRequest = membershipId
        ? axios.put(`users/memberships/${membershipId}/permissions/name`, {
            permissions: payload,
          })
        : axios.post(`users/${data?.id}/permissions/name${orgQuery}`, {
            permissions: payload,
          });
      saveRequest
        .then((res) => {
          toast.success("Permissions updated successfully");
          setOpen(false);
          refetchUsers();
          const saved = res?.data?.data?.permissions ?? payload;
          const normalizedSaved = saved.map((item) =>
            typeof item === "string" ? item : item?.name
          );
          if (onPermissionsSaved) {
            onPermissionsSaved(normalizedSaved.filter(Boolean));
          } else {
            setData((prev) => ({
              ...prev,
              permissions: saved,
              permittedModules: saved,
            }));
          }
        })
        .catch((err) => {
          toast.error(
            err?.response?.data?.errorMessage ||
              err?.response?.data?.message ||
              "Error updating permissions"
          );
        });
    } else {
      setData((prev) => ({
        ...prev,
        permissions: payload,
        permittedModules: payload,
      }));
      setOpen(false);
    }
  };

  const allSelected =
    allCatalogKeys.length > 0 && allCatalogKeys.every((key) => selectedKeys.includes(key));
  return (
    <Drawer
      anchor="right"
      open={open}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: "32%" },
          display: "flex",
          flexDirection: "column",
          height: "100%",
        },
      }}
    >
      <div className="flex-shrink-0 flex justify-between p-4 border-b">
        <div className="pr-4">
          <h1 className="text-xl font-semibold">
            Permissions (v2)
            {membershipContext?.orgName ? ` — ${membershipContext.orgName}` : ""}
          </h1>
          <p className="text-sm text-app-text-muted mt-1">
            Organisation permissions only. Platform permissions (consultancy org management) are
            managed separately.
          </p>
          {isUsingDefaults && (
            <p className="text-xs text-primary mt-1">
              Nothing saved yet — the standard case-work modules are pre-selected. Save to apply.
            </p>
          )}
        </div>
        <Close onClick={toggleDrawer} className="cursor-pointer flex-shrink-0" />
      </div>
      <div className="flex-shrink-0 px-4 pt-4">
        <div className="flex flex-wrap items-end gap-2 border-b pb-3">
          <TextField
            select
            size="small"
            label="Role defaults"
            value={presetRole}
            onChange={(e) => setPresetRole(e.target.value)}
            sx={{ minWidth: 190 }}
            helperText={findRolePreset(presetRole)?.summary}
            slotProps={{ formHelperText: { sx: { mx: 0, lineHeight: 1.4 } } }}
          >
            {ROLE_PRESETS.map((preset) => (
              <MenuItem key={preset.role} value={preset.role}>
                {preset.label}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            size="medium"
            disabled={!presetRole}
            onClick={() => setPendingPresetRole(presetRole)}
          >
            Apply defaults
          </Button>
        </div>
        {appliedPresetLabel && (
          <p className="text-xs text-primary pt-2">
            {appliedPresetLabel} defaults applied. Adjust anything you need, then Save to write
            them.
          </p>
        )}
        <div className="flex justify-between items-center border-b py-3">
          <span className="font-medium">Select all</span>
          <div className="flex items-center gap-2">
            <Button
              size="small"
              onClick={() =>
                setExpandedModules(
                  expandedModules.length ? [] : moduleGroups.map((group) => group.module)
                )
              }
            >
              {expandedModules.length ? "Collapse all" : "Expand all"}
            </Button>
            <Switch checked={allSelected} onChange={handleSelectAll} />
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
        <div className="space-y-2 pb-2">
          {moduleGroups.map((group) => {
            const moduleKeys = group.permissions.map((p) => p.name);
            const selectedCount = moduleKeys.filter((k) => selectedKeys.includes(k)).length;
            const moduleChecked = moduleKeys.length > 0 && selectedCount === moduleKeys.length;

            return (
              <Accordion
                key={group.module}
                disableGutters
                elevation={0}
                expanded={expandedModules.includes(group.module)}
                onChange={() => toggleModuleExpanded(group.module)}
                sx={{
                  border: "1px solid var(--app-border, #e2e8f0)",
                  borderRadius: "6px !important",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 48 }}>
                  <div className="flex flex-1 justify-between items-center gap-3 pr-2">
                    <div className="min-w-0">
                      <Typography variant="subtitle2" className="font-semibold uppercase">
                        {group.label}
                      </Typography>
                      <span className="text-xs text-app-text-muted">
                        {selectedCount} of {moduleKeys.length} enabled
                      </span>
                    </div>
                    <Tooltip title={moduleChecked ? "Turn all off" : "Turn all on"}>
                      <Switch
                        checked={moduleChecked}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                        onChange={(e) => toggleModule(group, e.target.checked)}
                      />
                    </Tooltip>
                  </div>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <div className="space-y-1 pl-2">
                    {[...group.permissions]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((perm) => (
                        <div key={perm.name} className="flex justify-between items-center text-sm">
                          <span className="flex items-center gap-1">
                            <span>
                              {formatPermissionLabel(perm.name)}
                              <span className="block text-xs text-app-text-muted font-mono">
                                {perm.name}
                              </span>
                            </span>
                            <Tooltip
                              title={getPermissionDescription(perm.name)}
                              arrow
                              placement="top"
                              slotProps={{
                                tooltip: {
                                  sx: {
                                    maxWidth: 460,
                                    fontSize: "0.75rem",
                                    lineHeight: 1.55,
                                    padding: "10px 12px",
                                  },
                                },
                              }}
                            >
                              <InfoOutlined
                                fontSize="inherit"
                                className="text-app-text-muted cursor-help flex-shrink-0"
                              />
                            </Tooltip>
                          </span>
                          <Switch
                            size="small"
                            checked={selectedKeys.includes(perm.name)}
                            onChange={() => toggleKey(perm.name)}
                          />
                        </div>
                      ))}
                  </div>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </div>
      </div>
      <div className="flex-shrink-0 p-4 border-t bg-app-surface">
        <div className="flex justify-between gap-4">
          <Button variant="outlined" onClick={toggleDrawer}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </div>
      </div>
      <ConfirmDialog
        open={Boolean(pendingPreset)}
        title={`Apply ${pendingPreset?.label ?? ""} defaults?`}
        message={
          <>
            Every toggle in this drawer is replaced by the {pendingPreset?.label} set, so any
            changes you have made here are discarded. Nothing is written until you press Save, and
            you can keep adjusting individual permissions afterwards.
          </>
        }
        confirmLabel="Replace toggles"
        onConfirm={() => applyPreset(pendingPresetRole)}
        onCancel={() => setPendingPresetRole(null)}
      />
    </Drawer>
  );
}
